import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import endOfDay from "date-fns/endOfDay";
import endOfWeek from "date-fns/endOfWeek";
import startOfDay from "date-fns/startOfDay";
import startOfWeek from "date-fns/startOfWeek";
import subWeeks from "date-fns/subWeeks";
import i18next from "i18next";

import axios from "../../../utils/api-client";
import { setSupervisorRateLimitError } from "./../../../features/user/user-slice";
import { MinHoursNotificationReportState } from "./min-hours-notification-report-types";

const initialState: MinHoursNotificationReportState = {
  reports: [],
  getReports: {
    status: "idle",
    errorMessage: "",
  },
  startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }).toISOString(),
  endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }).toISOString(),
};

export const getMinHoursNotificationHistory = createAsyncThunk(
  "min-hours-notification-report/get-history",
  async (
    { weekStart, weekEnd }: { weekStart: string; weekEnd: string },
    { dispatch, fulfillWithValue, rejectWithValue }
  ) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/Reports/GetMinHoursNotificationHistory?weekStart=${weekStart}&weekEnd=${weekEnd}`
      );
      // Backend returns 204 No Content for empty results, but axios might return empty string or null.
      // Based on absence-reports-slice.ts, it expects an array.
      return fulfillWithValue(res.data || []);
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      const status = error?.response?.status;
      if (status === 429) {
        dispatch(setSupervisorRateLimitError());
      }
      return rejectWithValue(errorResponse || error?.message || i18next.t("common_error-occurred"));
    }
  }
);

export const MinHoursNotificationReportSlice = createSlice({
  name: "minHoursNotificationReport",
  initialState,
  reducers: {
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMinHoursNotificationHistory.pending, (state) => {
      state.getReports.status = "pending";
    });
    builder.addCase(getMinHoursNotificationHistory.fulfilled, (state, action) => {
      state.reports = action.payload;
      state.getReports.status = "fulfilled";
    });
    builder.addCase(getMinHoursNotificationHistory.rejected, (state, action: any) => {
      state.getReports.status = "error";
      state.getReports.errorMessage = action.payload ?? i18next.t("common_error-occurred");
    });
  },
});

export const { setStartDate, setEndDate } = MinHoursNotificationReportSlice.actions;

export default MinHoursNotificationReportSlice.reducer;
